import { useIntl } from 'react-intl'

import { type ChannelId } from '@onr/schema'

import { type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { useChannelUnread } from '@/state/channels/useChannelUnread'

import { channelName } from './channelLabels'

export type MessagingDestination = 'teams' | 'channel'

export interface MessagingRailLinks {
  destination: MessagingDestination
  channelId: ChannelId
  teamsTo: string
  teamsParams?: Record<string, string>
  channelTo: string
  channelParams?: Record<string, string>
  search?: Record<string, unknown>
}

export interface UseMessagingRailOptions extends MessagingRailLinks {
  regionId: string
  eveningId: number | undefined
  teamsHaveUnread: boolean
}

const buildEntries = (
  labels: { teams: string; channel: string },
  { destination, teamsTo, teamsParams, channelTo, channelParams, search }: MessagingRailLinks,
  unread: { teams: boolean; channel: boolean }
): MessagingRailProps => ({
  entries: [
    {
      key: 'teams' as const,
      label: labels.teams,
      to: teamsTo,
      params: teamsParams,
      search,
      active: destination === 'teams',
      hasUnread: unread.teams,
    },
    {
      key: 'channel' as const,
      label: labels.channel,
      to: channelTo,
      params: channelParams,
      search,
      active: destination === 'channel',
      hasUnread: unread.channel,
    },
  ],
})

export const useMessagingRail = ({
  regionId,
  eveningId,
  teamsHaveUnread,
  ...links
}: UseMessagingRailOptions): MessagingRailProps => {
  const intl = useIntl()
  const channelHasUnread = useChannelUnread(regionId, links.channelId, eveningId)
  const teams = intl.formatMessage({ id: 'messaging.rail.teams' })
  const channel = channelName(intl, links.channelId)

  return buildEntries({ teams, channel }, links, { teams: teamsHaveUnread, channel: channelHasUnread })
}

/**
 * Rail for the closed-evening views: the unread dots track the current evening, so an archived
 * thread never carries one.
 */
export const useReadOnlyMessagingRail = (links: MessagingRailLinks): MessagingRailProps => {
  const intl = useIntl()
  const teams = intl.formatMessage({ id: 'messaging.rail.teams' })
  const channel = channelName(intl, links.channelId)

  return buildEntries({ teams, channel }, links, { teams: false, channel: false })
}
